import React, { useState, useRef } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

/**
 * Month selector component for picking timeline months
 */
const MonthSelector = ({ 
  value, 
  monthName, 
  fieldName, 
  shortName, 
  months,
  maxIndex = null,
  minIndex = null,
  onChangeSuccess = null
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedMonthRef = useRef(null);
  const popoverPanelRef = useRef(null);
  
  // Get months list from the API response or use defaults
  const monthOptions = months.length > 0 ? 
    months : 
    Array.from({ length: 24 }, (_, i) => {
      const date = new Date(2024, 3 + i, 1); // Starting April 2024
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    });
  
  // Function to scroll to the selected month
  const scrollToSelection = () => {
    // Use a slightly longer timeout to ensure the panel is fully rendered and positioned
    setTimeout(() => {
      if (selectedMonthRef.current && popoverPanelRef.current) {
        const panel = popoverPanelRef.current;
        const selectedItem = selectedMonthRef.current;
        const panelHeight = panel.clientHeight;
        const itemHeight = selectedItem.clientHeight;
        
        // Scroll to position (centered)
        panel.scrollTop = selectedItem.offsetTop - (panelHeight / 2) + (itemHeight / 2);
      }
    }, 100); // Increased timeout for more reliable scrolling
  };
  
  const handleMonthChange = async (monthIndex, isDisabled) => {
    // If disabled, show error message but don't proceed with the update
    if (isDisabled) {
      if (minIndex !== null && monthIndex < minIndex) {
        setError(`Cannot select a month before ${monthOptions[minIndex]}`);
      } else if (maxIndex !== null && monthIndex > maxIndex) {
        setError(`Cannot select a month after ${monthOptions[maxIndex]}`);
      }
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeField } = await import('../../utils/api');
      
      // Call the API to update the timeline month
      const result = await updateScopeField(shortName, fieldName, monthIndex);
      
      if (result.success) {
        // Call success callback if provided
        if (onChangeSuccess) {
          onChangeSuccess(monthIndex);
        }
      } else {
        setError('Failed to update month');
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      setError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="relative">
      <Popover className="relative">
        {({ open }) => {
          // Call scrollToSelection when the panel opens
          if (open) {
            scrollToSelection();
          }
          
          return (
            <>
              <PopoverButton
                className={`
                  block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 
                  focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 border border-gray-300
                  flex items-center justify-between ${isLoading ? 'opacity-50' : ''}
                `}
                disabled={isLoading}
              >
                <span className="truncate">{monthName || 'Select month'}</span>
                {isLoading ? (
                  <span className="text-xs animate-pulse">Loading...</span>
                ) : (
                  <CalendarIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                )}
              </PopoverButton>

              <PopoverPanel 
                ref={popoverPanelRef}
                className="absolute z-10 w-full bg-white rounded-md shadow-lg border border-gray-200 mt-1 py-1 max-h-60 overflow-auto"
              >
                {({ close }) => (
                  <div className="py-1">
                    {monthOptions.map((month, index) => {
                      // Determine if item should be disabled based on min/max constraints
                      const isDisabled = (minIndex !== null && index < minIndex) || 
                                       (maxIndex !== null && index > maxIndex);
                      
                      // For the selected month, add the ref
                      const isSelected = index === value;
                      
                      return (
                        <button
                          key={index}
                          ref={isSelected ? selectedMonthRef : null}
                          onClick={() => {
                            if (!isDisabled) {
                              handleMonthChange(index, isDisabled);
                              // Auto-close the popover on selection (unless disabled)
                              close();
                            } else {
                              // Just show error for disabled items
                              handleMonthChange(index, isDisabled);
                            }
                          }}
                          className={`
                            w-full text-left px-4 py-2 text-sm 
                            ${isSelected 
                              ? 'bg-indigo-50 text-indigo-700 font-medium' 
                              : isDisabled
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                            }
                          `}
                        >
                          {month}
                        </button>
                      );
                    })}
                  </div>
                )}
              </PopoverPanel>
            </>
          );
        }}
      </Popover>
      
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default MonthSelector;